import { useInfiniteQuery } from "@tanstack/react-query";
import { reactQueryCacheAndRefetchOptions } from "@";
import { getThemes } from "../api";
import { themesKeys } from "./keys";
import type { SearchValues, ThemesListResponse } from "../../types";

export const THEMES_PAGE_SIZE = 8;

/**
 * Infinite-scroll variant of the themes list. Used by the management page
 * (`/website/themes`) which shows themes as a card grid and loads the next
 * page as the user scrolls instead of paging through a table.
 */
export const useInfiniteThemesQuery = (search: SearchValues) => {
  return useInfiniteQuery<ThemesListResponse>({
    queryKey: [...themesKeys.all, "infinite", search] as const,
    queryFn: async ({ pageParam }) => {
      const { data } = await getThemes(
        pageParam as number,
        THEMES_PAGE_SIZE,
        search,
      );
      return data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce(
        (sum, page) => sum + (page.items?.length ?? 0),
        0,
      );
      return loaded < (lastPage.totalCount ?? 0)
        ? allPages.length + 1
        : undefined;
    },
    ...reactQueryCacheAndRefetchOptions,
  });
};
